import React, { useState, useEffect } from 'react';
import { fetchAuthSession, signInWithRedirect, signOut, getCurrentUser } from 'aws-amplify/auth';
import SpecDrivenPage from './components/SpecDrivenPage';
import AppListPage from './components/AppListPage';
import StandaloneDeployPage from './components/StandaloneDeployPage';
import LoginPage from './components/LoginPage';
import Header from './components/Header';
import { User } from './types';
import { getAuthMode, setAuthMode } from './services/api';
import './styles/App.css';

type Page = 'login' | 'list' | 'create' | 'edit' | 'standalone';

// Identity used when login is DISABLED (guest-admin mode) — the real m.imokawa
// admin. The backend independently injects the same identity for tokenless API
// calls, so the UI and API stay in sync.
const GUEST_ADMIN: User = {
  username: 'm.imokawa',
  email: 'm.imokawa@tcs.com',
  groups: ['admin', 'ea-team', 'development'],
  isAdmin: true,
  guest: true,
};

function App() {
  const [currentPage, setCurrentPage] = useState<Page>('login');
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingAppId, setEditingAppId] = useState<string | null>(null);
  const [loginDisabled, setLoginDisabled] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    // Server-side setting decides the mode. When login is disabled, land
    // straight in as the m.imokawa admin — no Cognito.
    const mode = await getAuthMode();
    setLoginDisabled(mode.loginDisabled);
    if (mode.loginDisabled) {
      setUser(GUEST_ADMIN);
      setCurrentPage('list');
      setLoading(false);
      return;
    }
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      const payload = session.tokens?.idToken?.payload || {};
      const groups = (payload['cognito:groups'] as string[]) || [];
      const email = currentUser.signInDetails?.loginId || (payload['email'] as string) || '';
      // Email-alias pools return the sub UUID as username — prefer something readable
      const looksLikeSub = /^[0-9a-f-]{30,}$/i.test(currentUser.username);

      setUser({
        username: looksLikeSub && email ? email.split('@')[0] : currentUser.username,
        email,
        groups,
        isAdmin: groups.includes('admin')
      });
      setCurrentPage('list');
    } catch (error) {
      // Not authenticated — require Cognito login.
      setUser(null);
      setCurrentPage('login');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = () => {
    signInWithRedirect();
  };

  const handleLogout = async () => {
    // In guest-admin mode there is no Cognito session to sign out of.
    if (!user?.guest) {
      await signOut();
    }
    setUser(null);
    setCurrentPage('login');
  };

  // Settings toggle: disable (guest-admin) or re-enable Cognito login. Persists
  // server-side so the change applies to everyone and both frontend + backend.
  const handleToggleLogin = async (disable: boolean) => {
    if (disable && !window.confirm(
      'Disable Cognito login? Anyone who opens this site will get full m.imokawa admin access (provisioning, deploy, keys). Continue?'
    )) return;
    try {
      const res = await setAuthMode(disable);
      setLoginDisabled(res.loginDisabled);
      if (res.loginDisabled) {
        setUser(GUEST_ADMIN);
        setCurrentPage('list');
      } else if (user?.guest) {
        // Login is required again and we were only a guest → go to Cognito login.
        setUser(null);
        setCurrentPage('login');
      }
    } catch (e: any) {
      alert('Failed to change login setting: ' + (e?.message || e));
    }
  };

  const handleCreateNew = () => {
    setEditingAppId(null);
    setCurrentPage('create');
  };

  const handleEditApp = (appId: string) => {
    setEditingAppId(appId);
    setCurrentPage('edit');
  };

  const handleBackToList = () => {
    setEditingAppId(null);
    setCurrentPage('list');
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="app">
      {user && (
        <Header
          user={user}
          onLogout={handleLogout}
          onNavigate={setCurrentPage}
          currentPage={currentPage}
          loginDisabled={loginDisabled}
          onToggleLogin={handleToggleLogin}
        />
      )}

      <main className="main-content">
        {currentPage === 'login' && (
          <LoginPage onLogin={handleLogin} />
        )}

        {currentPage === 'list' && user && (
          <AppListPage
            user={user}
            onCreateNew={handleCreateNew}
            onEditApp={handleEditApp}
            onStandaloneDeploy={() => setCurrentPage('standalone')}
          />
        )}

        {currentPage === 'standalone' && user && (
          <StandaloneDeployPage
            user={user}
            onBack={handleBackToList}
          />
        )}

        {(currentPage === 'create' || currentPage === 'edit') && user && (
          <SpecDrivenPage
            user={user}
            editingAppId={editingAppId}
            onBack={handleBackToList}
          />
        )}
      </main>
    </div>
  );
}

export default App;
